import { ChangeEvent, ClickEvent, Image, Tree, TreeNode } from "@mcleod/components";
import { MenuItem, ObjectFilterFunction, ObjectUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ModelPermsTree } from "../models/ModelPermsTree";
import { AutogenLayoutPermissionsMain } from "./autogen/AutogenLayoutPermissionsMain";
import { PanelPermsGrant } from "./PanelPermsGrant";
import { PermissionsSource } from "./PermissionsSource";
import { PermsContainer } from "./PermsContainer";
import { PermsMenuTreeNode } from "./PermsMenuTreeNode";
import { RowPermsSecureItem } from "../models/ModelPermsSecureItem";

interface PermMenuItem extends MenuItem {
    nestedLayoutPaths?: string[];
}

export class PermissionsMain extends AutogenLayoutPermissionsMain implements PermsContainer {
    panelGrant: PanelPermsGrant;
    permissionsSource: PermissionsSource;
    menuData: PermMenuItem;
    nonMenuData: PermMenuItem;
    actionPerms: PermMenuItem;
    apiPerms: PermMenuItem;
    mobileAppPerms: PermMenuItem;
    treeMenuCasted: Tree<PermsMenuTreeNode>;

    override async onLoad() {
        this.savePermsButton.enabled = false;
        this.treeMenuCasted = this.treeMenu as Tree<PermsMenuTreeNode>;
        this.treeMenuCasted.selectedNodeBackgroundColor = "primary.lightest";
        this.treeMenuCasted.selectedNodeColor = "default"
        this.switchPermsSet.allowIndeterminate = true;
        this.switchPermsSet.checked = undefined;
        this.switchPermsSet.addChangeListener(() => this.populateTree());
        this.textSearchMenu.imagePre = new Image({ name: ImageName.filter, color: "#9E9E9E", paddingLeft: 4, height: 28, width: 28 });
        await this.loadPerms();
        this.panelGrant = (this.layoutPermsGrant as PanelPermsGrant);
        this.panelGrant.permsContainer = this;
        this.panelGrant.scrollY = true;
        this.populateTree();
        this.addMountListener(() => this.textSearchMenu.focus());

        this.permissionsSource.addChangedDataCallback(hasChanged => {
            this.savePermsButton.enabled = hasChanged;
        });
    }

    async loadPerms() {
        await this.populatePermCollections();
        const menuData = await new ModelPermsTree().search();
        const response = menuData.modelRows[0];
        this.menuData = response.get("menu");
        this.nonMenuData = response.get("non_menu");
        this.actionPerms = response.get("actions").actions;
        this.apiPerms = response.get("api").api;
        this.mobileAppPerms = response.get("mobile_app").mobile_app;
    }

    syncMenuTreeNodes() {
        this.treeMenu.getRootNode().recurseChildren(node => {
            if (node instanceof PermsMenuTreeNode) {
                node.syncUI();
            }
        });
    }

    async populatePermCollections() {
        this.permissionsSource = new PermissionsSource();
        await this.permissionsSource.loadPermissionsData();
    }

    textSearchMenuOnChange(_event: any) {
        this.populateTree();
    }

    treeMenuOnChange(event: ChangeEvent) {
        this.menuItemSelected();
    }

    menuItemSelected() {
        const items = this.treeMenuCasted.selectedNodes?.map(node => node.secureItem);
        this.panelGrant.setItemsBeingEdited(items);
    }

    populateTree() {
        this.panelGrant.clear();
        if (this.menuData == null)
            return;

        const rootChildren = [
            this.createHeaderNode("Menu items", this.menuData.items, false),
            this.createHeaderNode("Non-menu items", this.nonMenuData.items, false),
            this.createHeaderNode("Actions", this.actionPerms.items, true),
            this.createHeaderNode("API", this.apiPerms.items, true),
            // Mobile App permissions are essentially Action permissions with a screen_class of "DriverApplication" instead of "Action".
            this.createHeaderNode("Mobile App", this.mobileAppPerms.items, true, RowPermsSecureItem.DRIVER_APPLICATION),
        ];
        for (let i = rootChildren.length - 1; i >= 0; i--)
            if (rootChildren[i].getChildCount() === 0)
                rootChildren.splice(i, 1);
        this.treeMenu.getRootNode().setChildren(rootChildren);
        if (this.filter != null)
            this.treeMenu.getRootNode().expandAll();
        this.treeMenu.selectedNode = null;
    }

    private createHeaderNode(text: string, items: PermMenuItem[], isAction: boolean, actionPath?: string): TreeNode {
        let filtered = items;
        const filter = this.filter;
        if (filter != null) {
            filtered = ObjectUtil.deepCopy(filtered);
            filtered = ObjectUtil.filterRecursiveArrayWithFunction<PermMenuItem>(filtered, "items", filter);
        }
        const result = new TreeNode({ text, selectable: false });
        for (const item of filtered)
            this.addTreeNode(result, item, isAction, actionPath);
        result.removeChildrenMatching(node => node.expandedImageName != null && node.getChildCount() === 0);
        this.addMenuItemContextMenu(result);
        return result;
    }

    private get filter(): ObjectFilterFunction<PermMenuItem>  {
        if (this.textSearchMenu.isEmpty() && this.switchPermsSet.checked == undefined) {
            return null;
        }
        return (item: PermMenuItem) => {
            let result = true;
            if (this.textSearchMenu.text.length > 0)
                result = item.caption?.toLowerCase().includes(this.textSearchMenu.text.toLowerCase());
            if (this.switchPermsSet.checked === false && result) {
                result = this.permissionsSource.getEntireScreenViewSecureItemRow(item.path) != null;
            } else if (this.switchPermsSet.checked === true && result) {
                const nestedPaths = new Set(item.nestedLayoutPaths ?? []);
                result = this.permissionsSource.getSecureItemRows(row => {
                    const targetPath = row.get("screen_class");
                    if (targetPath === item.path) {
                        // If it's the main layout we are checking, it MUST be field-level explicitly
                        return row.isFieldLevel();
                    } else if (nestedPaths.has(targetPath)) {
                        // If it's a nested layout, ANY permission (top-level or field-level)
                        // acts as a localized restriction on the parent!
                        return true;
                    }

                    return false;
                })?.length > 0;
            }
            return result;
        }
    }

    private addTreeNode(parent: TreeNode, item: PermMenuItem, isAction: boolean, actionPath?: string) {
        if (item.items == null) {
            const node = this.createMenuNode(item, isAction, actionPath);
            parent.addChild(node);
        }
        else {
            const node = new TreeNode({ text: item.caption, expandedImageName: ImageName.boxHide, collapsedImageName: ImageName.boxAdd, allowSelect: false, selectable: false });
            this.addMenuItemContextMenu(node);
            parent.addChild(node);
            for (const child of item.items)
                this.addTreeNode(node, child, isAction, actionPath);
        }
    }

    private addMenuItemContextMenu(node: TreeNode) {
        node.component.contextMenuItems = [
            { text: "Secure all sub items", onClick: () => {
                    const nodes = this.getPermsMenuTreeNodes(node);
                    nodes.forEach(node => this.permissionsSource.addSecureItemRow(node.secureItem));
                    this.treeMenu.selectedNodes = nodes;
            }},
            { text: "Unsecure all sub items", onClick: () => {
                    const nodes = this.getPermsMenuTreeNodes(node);
                    nodes.forEach(node => this.permissionsSource.deleteSecureItem(node.secureItem));
            }}
        ];

        node.component.addContextMenuCreationListener(() => {
            this.treeMenu.selectedNode = null;
            node.expanded = true;
            node.expandAll();
        });
    }

    private getPermsMenuTreeNodes(node: TreeNode): PermsMenuTreeNode[] {
        return node.getRecursiveChildNodes(node => node instanceof PermsMenuTreeNode) as PermsMenuTreeNode[];
    }

    private createMenuNode(item: PermMenuItem, isAction: boolean, actionPath?: string): PermsMenuTreeNode {
        const result = new PermsMenuTreeNode(item.path, isAction, actionPath, {
            title: item.caption,
            nestedLayoutPaths: item.nestedLayoutPaths,
            permissionsMain: this
        });
        return result;
    }

    /** This is an event handler for the onClick event of savePermsButton. */
    savePermsButtonOnClick(event: ClickEvent) {
        this.savePermsButton.busy = true;
        const savePromise = this.permissionsSource.post().then(() => {
            this.savePermsButton.enabled = false;
        }).finally(() => {
            this.savePermsButton.busy = false
        });
        this.addBusyOverlay(savePromise);
    }
}
